import { Component, type ErrorInfo, type ReactNode } from "react";

export type ErrorBoundaryProps = {
  /** Shown in place of the children after they fail. A function gets a way to try again. */
  fallback: ReactNode | ((retry: () => void) => ReactNode);
  /** When this changes, a failed boundary tries its children again, such as on opening another chat. */
  resetKey?: unknown;
  /** Hears about the failure, to report it. */
  onError?: (error: unknown, info: ErrorInfo) => void;
  children?: ReactNode;
};

type State = { failed: boolean };

/**
 * Keeps a failure in one part of the screen from taking down the rest. A message that cannot be
 * drawn shows a short notice, and the rest of the chat carries on.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    this.props.onError?.(error, info);
  }

  componentDidUpdate(prev: ErrorBoundaryProps) {
    if (this.state.failed && !Object.is(prev.resetKey, this.props.resetKey)) this.setState({ failed: false });
  }

  retry = () => this.setState({ failed: false });

  render() {
    if (!this.state.failed) return this.props.children;
    const { fallback } = this.props;
    return typeof fallback === "function" ? fallback(this.retry) : fallback;
  }
}
